import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import type { ModuleKey, AccessLevel } from "@/hooks/useOrgPermissions";
import type { LucideIcon } from "lucide-react";
import { UpgradeDialog } from "@/components/Billing/UpgradeDialog";

interface Props {
  module: ModuleKey;
  level: AccessLevel;
  icon: LucideIcon;
  label: string;
  view: string;
  activeView: string;
  onViewChange: (v: string) => void;
  orgId: string;
}

export function PermissionGatedNavItem({
  module, level, icon: Icon, label, view, activeView, onViewChange, orgId,
}: Props) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const { data: userId } = useQuery({
    queryKey: ["current-user-id"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id ?? null;
    },
    staleTime: Infinity,
  });

  const { data: permitted, isLoading: permLoading } = useQuery({
    queryKey: ["perm", orgId, module, level, userId],
    enabled: !!orgId && !!userId,
    queryFn: async () => {
      const { data } = await (supabase.rpc("has_permission" as any, {
        _org_id:   orgId,
        _user_id:  userId,
        _module:   module,
        _required: level,
      }) as any);
      return data as boolean;
    },
  });

  const { data: planAllows, isLoading: planLoading } = useQuery({
    queryKey: ["plan-module", orgId, module, userId],
    enabled: !!orgId && !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await (supabase.rpc("module_available" as any, {
        p_org_id: orgId,
        p_module: module,
      }) as any);
      return data as boolean;
    },
  });

  const isLoading = permLoading || planLoading;
  const isLimited = !planLoading && planAllows === false;
  const hasPermission = permitted === true;

  // Hide entirely only when we know there's no permission AND no plan restriction
  if (!isLoading && !isLimited && !hasPermission) return null;

  // Plan-limited: show UPGRADE badge (greyed out, clickable for upgrade dialog)
  if (isLimited) {
    return (
      <>
        <div
          role="button"
          onClick={() => setUpgradeOpen(true)}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-muted-foreground/50 cursor-pointer hover:bg-muted/40 transition-colors"
        >
          <Icon size={15} className="flex-shrink-0" />
          <span className="text-[12.5px] flex-1">{label}</span>
          <span className="text-[9px] font-bold bg-[#C8A84B]/20 text-[#C8A84B] px-1.5 py-0.5 rounded-full flex-shrink-0">
            UPGRADE
          </span>
        </div>
        <UpgradeDialog
          module={module}
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          onNavigate={onViewChange}
        />
      </>
    );
  }

  const isActive = activeView === view;
  return (
    <button
      onClick={() => onViewChange(view)}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-base text-sm font-medium",
        isActive
          ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
          : "text-sidebar-foreground hover:bg-sidebar-accent/50"
      )}
    >
      <Icon className="w-5 h-5" />
      {label}
    </button>
  );
}
